import type { HelpView } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/session';
import { supportUi } from '../lib/support';
import { HelpPilot } from '../pages/support/HelpPilot';
import { Button } from '../ui/button';
import { Sheet } from '../ui/dialog';
import { ErrorBox, Spinner } from '../ui/feedback';

/**
 * Кнопка «Спросить Pilot» в шапке (П0 «запустить Pilot», 03.11.2026, чат ai): тот же Pilot, что в «Помощи» — отвечает
 * по инструкции к этой странице и статьям базы знаний в правах человека, ответ помечен «Подготовлено ИИ». Если Pilot
 * недоступен, панель честно говорит почему: нет права на ИИ-помощника или модель ещё не прошла контрольную проверку.
 */
export function AiPanel({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Спросить Pilot"
      description="Помощник портала: отвечает по инструкциям и базе знаний"
    >
      {open ? <AiBody onClose={() => onOpenChange(false)} /> : null}
    </Sheet>
  );
}

/** Тело Pilot: в панели шапки и на странице «Pilot» из меню */
export function AiBody({ onClose }: { onClose?: () => void }) {
  const me = useMe();
  const canAi = useCan('ai.use');
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Тот же запрос, что у «Помощи»: сервер сообщает, можно ли спросить Pilot (модель допущена и есть право)
  const q = useQuery({
    queryKey: ['support', 'help', path],
    queryFn: () => api<HelpView>('/support/help', { query: { path } }),
    staleTime: 5 * 60_000,
  });
  const staff = me.tenant?.staff ?? false;
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (q.data.pilot.available) return <HelpPilot path={path} staff={staff} />;
  const openHelp = () => {
    onClose?.();
    supportUi.openHelp();
  };
  return (
    <div className="flex flex-col gap-4 text-[14px] text-ink">
      <div className="visor rounded-lg bg-surface-2 p-4">
        {!staff ? (
          <p>
            Pilot отвечает сотрудникам компании. Вопрос можно задать контактному лицу в компании, которая вас
            пригласила.
          </p>
        ) : !canAi ? (
          <p>
            В вашей роли нет права «Пользоваться ИИ-помощником». Его выдаёт администратор компании:
            «Администрирование → Роли».
          </p>
        ) : (
          <p>
            Pilot сейчас недоступен: модель ещё не прошла контрольную проверку или не подключена. Владелец
            портала видит итог проверки в кабинете портала, вкладка «Pilot».
          </p>
        )}
      </div>
      <p className="text-ink-2">Пока ответит инструкция к этой странице и поддержка.</p>
      <div>
        <Button onClick={openHelp}>Открыть помощь</Button>
      </div>
    </div>
  );
}
